"use client";

import { Monitor, MonitorSmartphone, Smartphone, Tablet } from "lucide-react";
import { useState, useEffect } from "react";

import { cn } from "@/app/_module/lib/utils";
import { Button } from "@/app/_module/components/ui/button";
import { Badge } from "@/app/_module/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { useToast } from "@/app/_module/hooks/use-toast";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  ResponsiveAlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";
import { focusRing } from "@/app/(authenticated)/settings/a2p-verification/_components/overview/primitives";

interface LoginActivity {
  id: string;
  deviceType: "desktop" | "mobile" | "tablet";
  deviceName: string;
  browser: string;
  os: string;
  ipAddress: string;
  location?: string;
  loginTime: string;
  lastActivity: string;
  isCurrent: boolean;
  isActive: boolean;
}

const DEVICE_ICONS = { mobile: Smartphone, tablet: Tablet, desktop: Monitor } as const;

export const SecurityActivity = function () {
  const { toast } = useToast();
  const [loginActivities, setLoginActivities] = useState<LoginActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [signingOutSessionId, setSigningOutSessionId] = useState<string | null>(null);

  useEffect(() => {
    fetchLoginActivity();
  }, []);

  const parseUserAgent = (userAgent: string) => {
    // ⚠️ iPhone UAs contain "like Mac OS X" — iOS/Android MUST be checked
    // BEFORE macOS or the phone reads as "macOS". And Capacitor's WKWebView
    // carries no "Safari/" token, so an app session parsed as "Unknown
    // Browser" — a webview UA on a phone IS our app.
    const isIOS = userAgent.includes("iPhone") || userAgent.includes("iPad") || userAgent.includes("iOS");
    const isAndroid = userAgent.includes("Android");
    const isWebView =
      (isIOS && !userAgent.includes("Safari/") && !userAgent.includes("CriOS/")) ||
      (isAndroid && userAgent.includes("wv"));

    let browser = "Unknown Browser";
    if (isWebView) browser = "InsuraCentral App";
    else if (userAgent.includes("Firefox/")) browser = "Firefox";
    else if (userAgent.includes("Edg/")) browser = "Edge";
    else if (userAgent.includes("CriOS/")) browser = "Chrome";
    else if (userAgent.includes("Chrome/")) browser = "Chrome";
    else if (userAgent.includes("Safari/") && !userAgent.includes("Chrome/")) browser = "Safari";
    else if (userAgent.includes("Opera/") || userAgent.includes("OPR/")) browser = "Opera";

    let os = "Unknown OS";
    if (isIOS) os = "iOS";
    else if (isAndroid) os = "Android";
    else if (userAgent.includes("Windows NT 10.0")) os = "Windows 10";
    else if (userAgent.includes("Windows NT 11.0")) os = "Windows 11";
    else if (userAgent.includes("Windows")) os = "Windows";
    else if (userAgent.includes("Mac OS X")) {
      const match = userAgent.match(/Mac OS X ([\d_]+)/);
      os = match ? `macOS ${match[1].replace(/_/g, ".")}` : "macOS";
    } else if (userAgent.includes("Linux")) os = "Linux";

    let deviceType: "desktop" | "mobile" | "tablet" = "desktop";
    if (userAgent.includes("iPad") || userAgent.includes("Tablet")) deviceType = "tablet";
    else if (userAgent.includes("Mobile") || isAndroid || isIOS) deviceType = "mobile";

    const deviceName = isWebView
      ? `InsuraCentral App (${os})`
      : `${browser} on ${os}`;
    return { browser, os, deviceType, deviceName };
  };

  const enhanceActivityData = (activity: LoginActivity): LoginActivity => {
    if (
      activity.browser === "Unknown" ||
      activity.deviceName === "Unknown" ||
      activity.os === "Unknown"
    ) {
      if (activity.isCurrent) {
        const parsed = parseUserAgent(navigator.userAgent);
        return {
          ...activity,
          browser: activity.browser === "Unknown" ? parsed.browser : activity.browser,
          os: activity.os === "Unknown" ? parsed.os : activity.os,
          deviceType: parsed.deviceType || activity.deviceType,
          deviceName:
            activity.deviceName === "Unknown" || activity.deviceName.includes("Unknown")
              ? parsed.deviceName
              : activity.deviceName,
        };
      }
      if (activity.deviceName === "Unknown on Unknown" || activity.browser === "Unknown") {
        let genericName = "Unknown Device";
        if (activity.deviceType === "mobile") genericName = "Mobile Device";
        else if (activity.deviceType === "tablet") genericName = "Tablet Device";
        else if (activity.deviceType === "desktop") genericName = "Desktop Device";
        return {
          ...activity,
          deviceName: genericName,
          browser: activity.browser === "Unknown" ? "Unknown Browser" : activity.browser,
          os: activity.os === "Unknown" ? "Unknown OS" : activity.os,
        };
      }
    }
    return activity;
  };

  const deduplicateSessions = (activities: LoginActivity[]): LoginActivity[] => {
    const sessionMap = new Map<string, LoginActivity>();
    const sorted = [...activities].sort(
      (a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime()
    );
    for (const activity of sorted) {
      const fingerprint =
        activity.ipAddress === "127.0.0.1" || activity.ipAddress === "::1"
          ? `localhost-${activity.browser}-${activity.os}`
          : `${activity.browser}-${activity.os}-${activity.ipAddress}`;
      if (!sessionMap.has(fingerprint)) {
        sessionMap.set(fingerprint, activity);
      } else {
        const existing = sessionMap.get(fingerprint)!;
        const existingTime = new Date(existing.lastActivity).getTime();
        const currentTime = new Date(activity.lastActivity).getTime();
        if (currentTime > existingTime) sessionMap.set(fingerprint, activity);
        else if (currentTime === existingTime && activity.isCurrent && !existing.isCurrent)
          sessionMap.set(fingerprint, activity);
      }
    }
    return Array.from(sessionMap.values()).sort(
      (a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime()
    );
  };

  const fetchLoginActivity = async () => {
    try {
      setIsLoading(true);
      setLoadError(false);
      const userProfileResponse = await api.get({
        url: getApiUrl(endpoint.user.getUserProfile),
        auth: true,
      });
      const userId =
        (userProfileResponse.data as any)?.user?.id ||
        (userProfileResponse.data as any)?.enrichedUser?.userId;
      if (!userId) throw new Error("User ID not found");

      const response = await api.get({
        url: `${getApiUrl(endpoint.auth.getLoginActivity)}?userId=${userId}`,
        auth: true,
      });
      const activities = (response as any)?.data || [];
      const enhanced = activities.map((a: LoginActivity) => enhanceActivityData(a));
      setLoginActivities(deduplicateSessions(enhanced));
    } catch {
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOutSession = async (sessionId: string) => {
    try {
      setSigningOutSessionId(sessionId);
      await api.delete({
        url: getApiUrl(endpoint.auth.signOutSession(sessionId)),
        auth: true,
      });
      toast({ title: "Device signed out" });
      await fetchLoginActivity();
    } catch (error: any) {
      toast({
        title: "Couldn't sign out that device",
        description: error?.response?.data?.message || error?.message || "Try again.",
        variant: "destructive",
      });
    } finally {
      setSigningOutSessionId(null);
    }
  };

  const signOutAllDevices = async () => {
    try {
      setIsSigningOut(true);
      const userProfileResponse = await api.get({
        url: getApiUrl(endpoint.user.getUserProfile),
        auth: true,
      });
      const userId =
        (userProfileResponse.data as any)?.user?.id ||
        (userProfileResponse.data as any)?.enrichedUser?.userId;
      if (!userId) throw new Error("User ID not found");

      await api.post({
        url: getApiUrl(endpoint.auth.signOutAllDevices),
        body: { userId },
        auth: true,
      });
      toast({ title: "Signed out everywhere", description: "Taking you to the sign-in page." });
      setTimeout(() => {
        window.location.href = "/auth/sign-in?logged_out=true";
      }, 2000);
    } catch {
      toast({
        title: "Couldn't sign out everywhere",
        description: "Try again.",
        variant: "destructive",
      });
    } finally {
      setIsSigningOut(false);
      setShowSignOutDialog(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const activeSessions = loginActivities.filter((a) => a.isActive);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <MonitorSmartphone className="h-5 w-5" />
            <CardTitle>Security & Activity</CardTitle>
          </div>
          <CardDescription>
            Devices that have signed in to your account. If you don't recognize one, sign it out and change your
            password.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {isLoading ? (
            <ul className="divide-y rounded-lg border" aria-busy="true" aria-label="Loading your devices">
              {[0, 1, 2].map((i) => (
                <li key={i} className="flex items-center gap-3 p-3">
                  <div className="h-5 w-5 shrink-0 animate-pulse rounded bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-44 max-w-full animate-pulse rounded bg-muted" />
                    <div className="h-3 w-28 animate-pulse rounded bg-muted" />
                  </div>
                </li>
              ))}
            </ul>
          ) : loadError ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
              <p className="text-sm text-muted-foreground">Couldn't load your devices.</p>
              <Button variant="outline" size="sm" onClick={fetchLoginActivity} className={cn("h-11 sm:h-9", focusRing)}>
                Try again
              </Button>
            </div>
          ) : loginActivities.length === 0 ? (
            <p className="text-sm text-muted-foreground">No sign-ins to show yet.</p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {loginActivities.map((a) => {
                const DeviceIcon = DEVICE_ICONS[a.deviceType] ?? Monitor;
                return (
                  <li key={a.id} className="flex items-center gap-3 p-3">
                    <DeviceIcon className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className={cn("truncate font-medium", !a.isActive && "text-muted-foreground")}>
                          {a.deviceName}
                        </span>
                        {a.isCurrent && (
                          <Badge variant="outline" className="border-primary/40 text-primary">
                            This device
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {a.location && <>{a.location} · </>}
                        {a.isCurrent ? (
                          "Active now"
                        ) : (
                          <>
                            {a.isActive ? "Last active " : "Signed out · last active "}
                            <time dateTime={a.lastActivity} title={new Date(a.lastActivity).toLocaleString()}>
                              {formatDate(a.lastActivity)}
                            </time>
                          </>
                        )}
                      </p>
                    </div>
                    {a.isActive && !a.isCurrent && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSignOutSession(a.id)}
                        disabled={signingOutSessionId === a.id}
                        aria-label={`Sign out ${a.deviceName}`}
                        className={cn("h-11 shrink-0 sm:h-9", focusRing)}
                      >
                        {signingOutSessionId === a.id ? "Signing out…" : "Sign out"}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {!isLoading && !loadError && activeSessions.length > 1 && (
            <Button
              variant="outline"
              onClick={() => setShowSignOutDialog(true)}
              className={cn("h-11 w-full text-destructive hover:text-destructive sm:h-10 sm:w-auto", focusRing)}
            >
              Sign out everywhere
            </Button>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showSignOutDialog} onOpenChange={setShowSignOutDialog}>
        <ResponsiveAlertDialogContent size="md">
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out everywhere?</AlertDialogTitle>
            <AlertDialogDescription>
              This signs you out on every device, including this one, and you'll need to sign in again. Do this if
              you think someone else has access to your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSigningOut}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={signOutAllDevices}
              disabled={isSigningOut}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isSigningOut ? "Signing out…" : "Sign out everywhere"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </ResponsiveAlertDialogContent>
      </AlertDialog>
    </>
  );
};
